import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import UserMenu from "./UserMenu";
import BreakingNewsTicker from "./BreakingNewsTicker";
import { supabase } from "@/lib/supabase";
import { BRAND_LOGO_URL, BRAND_NAME } from "@/constants/brand";
import { useBrandSettings } from "@/hooks/useBrandSettings";

const menuItems = [
  { label: "Início", path: "/" },
  { label: "Notícias", path: "/noticias" },
  { label: "Mercado da Bola", path: "/mercado-da-bola" },
  { label: "Jogos", path: "/jogos" },
  { label: "Brasileirão", path: "/brasileirao" },
  { label: "Internacional", path: "/internacional" },
  { label: "Botafogo TV", path: "/botafogo-tv" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [liveActive, setLiveActive] = useState(false);

  const searchRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { logoSize } = useBrandSettings();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    async function carregarStatusAoVivo() {
      const { data } = await supabase
        .from("transmissoes")
        .select("ativo, url, scheduled_start_at, scheduled_end_at")
        .eq("id", 1)
        .single();

      if (data) {
        const now = Date.now();
        const startsAt = data.scheduled_start_at
          ? new Date(data.scheduled_start_at).getTime()
          : null;
        const endsAt = data.scheduled_end_at
          ? new Date(data.scheduled_end_at).getTime()
          : null;
        const isWithinSchedule = (!startsAt || now >= startsAt) && (!endsAt || now <= endsAt);
        setLiveActive(data.ativo === true && Boolean(data.url) && isWithinSchedule);
      }
    }

    carregarStatusAoVivo();

    const interval = setInterval(carregarStatusAoVivo, 30000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (searchOpen && searchRef.current) {
      setTimeout(() => {
        searchRef.current?.focus();
      }, 50);
    }
  }, [searchOpen]);

  const handleSearch = () => {
    const term = searchInput.trim();

    if (!term) return;

    navigate(`/busca?q=${encodeURIComponent(term)}`);

    setSearchOpen(false);
    setSearchInput("");
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Enter") {
      handleSearch();
    }

    if (e.key === "Escape") {
      setSearchOpen(false);
      setSearchInput("");
    }
  };

  const desktopMenu = liveActive
    ? [...menuItems, { label: "Ao Vivo", path: "/ao-vivo" }]
    : menuItems;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white shadow-md" : "bg-black"
        }`}
      >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-20">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 cursor-pointer"
          >
            <img
              src={BRAND_LOGO_URL}
              alt={BRAND_NAME}
              className="object-contain"
              style={{ width: logoSize, height: logoSize }}
            />

            <div className="hidden md:block">
              <h1
                className={`font-serif font-bold text-xl transition-colors ${
                  scrolled
                    ? "text-alvinegro-black"
                    : "text-white"
                }`}
              >
                {BRAND_NAME}
              </h1>

              <p
                className={`text-xs transition-colors ${
                  scrolled
                    ? "text-alvinegro-muted"
                    : "text-white/70"
                }`}
              >
                Tudo sobre o Glorioso
              </p>
            </div>
          </button>

          <div className="hidden lg:flex items-center gap-6">
            {desktopMenu.map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`text-sm font-medium whitespace-nowrap transition-colors hover:opacity-80 cursor-pointer ${
                  scrolled
                    ? "text-alvinegro-dark"
                    : "text-white"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            {liveActive && (
              <button
                onClick={() => navigate("/ao-vivo")}
                className="hidden md:flex items-center gap-1.5 bg-red-500 text-white px-3 py-1.5 rounded-full text-sm font-medium hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                Ao Vivo
              </button>
            )}

            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors cursor-pointer ${
                scrolled
                  ? "bg-alvinegro-light text-alvinegro-dark"
                  : "bg-white/20 text-white"
              }`}
            >
              <i className="ri-search-line text-lg" />
            </button>

            <div className={scrolled ? "" : "text-white"}>
              <UserMenu />
            </div>

            <button
              onClick={() =>
                setMobileMenuOpen(!mobileMenuOpen)
              }
              className={`lg:hidden w-10 h-10 rounded-full flex items-center justify-center transition-colors cursor-pointer ${
                scrolled
                  ? "bg-alvinegro-light text-alvinegro-dark"
                  : "bg-white/20 text-white"
              }`}
            >
              <i
                className={`ri-${
                  mobileMenuOpen ? "close" : "menu"
                }-line text-lg`}
              />
            </button>
          </div>
        </div>
      </div>

      {searchOpen && (
        <div className="bg-white border-b border-alvinegro-border px-4 py-3">
          <div className="max-w-2xl mx-auto flex items-center gap-2">
            <div className="w-10 h-10 flex items-center justify-center text-alvinegro-muted">
              <i className="ri-search-line text-lg" />
            </div>

            <input
              ref={searchRef}
              type="text"
              value={searchInput}
              onChange={(e) =>
                setSearchInput(e.target.value)
              }
              onKeyDown={handleKeyDown}
              placeholder="Buscar notícias, jogadores, vídeos..."
              className="flex-1 px-3 py-2 rounded-full text-sm text-alvinegro-black placeholder-alvinegro-muted focus:outline-none bg-alvinegro-light"
            />

            <button
              onClick={handleSearch}
              className="bg-alvinegro-black text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-alvinegro-dark transition-colors cursor-pointer whitespace-nowrap"
            >
              Buscar
            </button>

            <button
              onClick={() => {
                setSearchOpen(false);
                setSearchInput("");
              }}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-alvinegro-light transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-alvinegro-muted" />
            </button>
          </div>
        </div>
      )}

      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-alvinegro-border shadow-lg">
          <div className="px-4 py-4 space-y-1">
            {desktopMenu.map((item) => (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setMobileMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-3 text-alvinegro-dark font-medium hover:bg-alvinegro-light rounded-lg transition-colors cursor-pointer"
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}
      </nav>

      <BreakingNewsTicker />
    </>
  );
}
